import { Fragment } from "preact";
import { useState, useCallback, useEffect, useRef } from "preact/hooks";
import LogLine from "./LogLine";
import LogGroup, { groupLogMessages } from "./LogGroup";
import { putEvent } from "../api";
import { getUserAvatar } from "../avatars";
import type { State } from "../state";
import { getUsedEmojis } from "../state";
import { BrutalButton, BrutalInput } from "./BrutalUI";

interface ChatPanelProps {
  state: State;
  slug: string;
  username: string;
  /** Last event id seen in previous session; show a separator line below it for the whole calendar session. */
  lastReadEventId?: number;
}

export default function ChatPanel({ state, slug, username, lastReadEventId = 0 }: ChatPanelProps) {
  const [body, setBody] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.logMessages?.length]);

  const sendChat = useCallback(
    (e: Event) => {
      e.preventDefault();
      if (!body.trim()) return;
      const msg = body.trim();
      putEvent(slug, {
        type: "chat_message_posted",
        message: msg,
        data: { body: msg },
      }).then(() => setBody(""));
    },
    [body, slug, username],
  );

  const messages = state.logMessages || [];
  const grouped = groupLogMessages(messages);
  const usedEmojis = getUsedEmojis(state);
  const avatarsByUsername = state.avatarsByUsername || {};

  /** Max event id in this item (single entry or group). */
  const maxIdOf = (item: import("../state").LogEntry | import("../state").LogEntry[]): number =>
    Array.isArray(item) ? Math.max(...item.map((e) => e.id)) : item.id;

  const showSeparator = lastReadEventId > 0;

  return (
    <div className="flex flex-col border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 min-h-[280px] max-h-[calc(100vh-12rem)] flex-shrink-0 w-full lg:w-80">
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-2">
        {grouped.map((item) => {
          const maxId = maxIdOf(item);
          const showLineAfter = showSeparator && maxId === lastReadEventId;
          return (
            <Fragment key={Array.isArray(item) ? `group-${item[0].id}` : item.id}>
              {Array.isArray(item) ? (
                <LogGroup
                  entries={item}
                  slug={slug}
                  username={username}
                  reactionsByMessage={state.reactionsByMessage}
                  reactionsByUserByMessage={state.reactionsByUserByMessage}
                  usedEmojis={usedEmojis}
                  avatarsByUsername={avatarsByUsername}
                />
              ) : (
                <LogLine
                  entry={item}
                  slug={item.type === "chat_message_posted" ? slug : undefined}
                  username={item.type === "chat_message_posted" ? username : undefined}
                  reactionCounts={
                    item.type === "chat_message_posted"
                      ? state.reactionsByMessage?.[item.id]
                      : undefined
                  }
                  currentUserEmojis={
                    item.type === "chat_message_posted" && username
                      ? (() => {
                          const v = state.reactionsByUserByMessage?.[item.id]?.[username];
                          return Array.isArray(v) ? v : v ? [v] : [];
                        })()
                      : undefined
                  }
                  reactionsByUser={
                    item.type === "chat_message_posted"
                      ? state.reactionsByUserByMessage?.[item.id]
                      : undefined
                  }
                  usedEmojis={usedEmojis}
                  avatar={getUserAvatar(item.username ?? "", avatarsByUsername)}
                />
              )}
              {showLineAfter && (
                <div
                  className="flex items-center gap-2 py-1 text-xs text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                >
                  <span className="flex-1 border-b-2 border-gray-300 dark:border-gray-600" />
                  <span className="font-logo uppercase tracking-tight shrink-0">Last seen</span>
                  <span className="flex-1 border-b-2 border-gray-300 dark:border-gray-600" />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <form
        className="flex gap-2 p-4 border-t-2 border-black dark:border-gray-100"
        onSubmit={sendChat}
      >
        <BrutalInput
          type="text"
          className="flex-1 min-w-0"
          placeholder="Type a message..."
          value={body}
          onInput={(e) => setBody((e.target as HTMLInputElement).value)}
        />
        <BrutalButton
          type="submit"
          variant="primary"
          className="px-5 py-2.5"
        >
          Send
        </BrutalButton>
      </form>
    </div>
  );
}
